import Button from "components/Button/Button";
import { Input } from "components/inputs/Input/Input";
import { Select } from "components/inputs/Select/Select";
import { TextArea } from "components/inputs/TextArea";
import ScrollingContainer from "components/ScrollContainer/ScrollingContainer";
import { MAX_CHARACTERS_IN_ASSET_DESCRIPTION, MAX_CHARACTERS_IN_ASSET_NAME, MIN_CHARACTERS_IN_ASSET_NAME } from "config/constants";
import { useBeaconlistStore, useUnregisteredBeaconStore, useUserRolePermissionStore } from "contexts/RootStore.context";
import { Grid, Horizontal, Vertical } from "gls"
import useDevice from "hooks/useDevice";
import { observer } from "mobx-react";
import { useMemo, useState } from "react";
import { colours } from "styling/colours";
import { style } from "typestyle";
import { alertErrorMessage, alertSuccessMessage } from "utils/alerts";
import { MinimumLengthError, RequiredValidationError } from "utils/formstate";
import { AssetPermissions } from "views/config/roles/RoleConfigDto";

export const NewBeaconAssetForm = observer(() => {
  const { isMobileDevice } = useDevice();
  const { getAssetTypes, getAssetOwners } = useUserRolePermissionStore()
  const {
    selectedBeaconToCreateAsset,
    setSelectedBeaconToCreateAsset,
    setSearchTerm,
    beaconForm,
    importAndCreate,
    loadUnregisteredBeacons
  } = useUnregisteredBeaconStore()
  const { loadBeacons } = useBeaconlistStore()
  const [loading, setLoading] = useState(false)

  const { asset_types, asset_owners } = useMemo(() => {
    return {
      asset_types: getAssetTypes(AssetPermissions.manage),
      asset_owners: getAssetOwners(AssetPermissions.manage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const onImportAndCreate = () => {
    setLoading(true)

    importAndCreate()
      .then(() => {
        alertSuccessMessage(`1 New Beacon has been imported & created an asset`);
        setSearchTerm('')
        setSelectedBeaconToCreateAsset(null)
        loadUnregisteredBeacons(true)
        loadBeacons(true)
      })
      .catch((error) => {
        if (error === 'validationError') {
          setLoading(false)
          return;
        }

        alertErrorMessage('Error importing beacon');
        setSearchTerm('')
        setSelectedBeaconToCreateAsset(null)
        loadUnregisteredBeacons(true)
      })
  }

  return (
    <ScrollingContainer>
      <Vertical className={assetFormStyle(isMobileDevice)} id="beacon-info-div">
        <h4>BEACON INFORMATION</h4>
        <table cellPadding={10}>
          <tr>
            <td width={'150px'} className="title">Beacon ID:</td>
            <td width={'250px'}>{selectedBeaconToCreateAsset?.beacon_id}</td>
            <td width={'100px'} className="title" rowSpan={3}>Notes:</td>
            <td rowSpan={3}>{selectedBeaconToCreateAsset?.notes}</td>
          </tr>
          <tr>
            <td className="title">Beacon Type:</td>
            <td>{selectedBeaconToCreateAsset?.beacon_type}</td>
          </tr>
          <tr>
            <td className="title">Order ID:</td>
            <td>{selectedBeaconToCreateAsset?.order_id}</td>
          </tr>
        </table>
        <div className={seperatorStyle} />
        <h4>ASSET INFORMATION</h4>
        <Grid>
          <Input
            label="Asset Name:"
            required
            className={fieldStyle}
            value={beaconForm.$.asset_name.value}
            onChange={(value) => { beaconForm.$.asset_name.onChange(value) }}
            // disabled={!props.canManageRoles}
            id="asset_name"
            name="asset_name"
            maxLength={MAX_CHARACTERS_IN_ASSET_NAME}
            minLength={MIN_CHARACTERS_IN_ASSET_NAME}
            showMaxLength
            error={beaconForm.$.asset_name.error !== RequiredValidationError && beaconForm.$.asset_name.error !== MinimumLengthError
              ? beaconForm.$.asset_name.error : ""}
          />
          <Select
            label="Asset Types"
            required
            id="asset_types_select"
            options={asset_types.map(r => ({ key: r.label, value: r.value }))}
            value={beaconForm.$.asset_type.value || []}
            onChange={beaconForm.$.asset_type.onChange}
            className={fieldStyle}
          />
        </Grid>
        <Select
          label="Owners"
          id="asset_owners_select"
          required
          options={asset_owners.map(r => ({ key: r.label, value: r.value }))}
          value={beaconForm.$.owner.value}
          onChange={beaconForm.$.owner.onChange}
          className={fieldStyle}
        />
        <TextArea
          label="Description:"
          value={beaconForm.$.description.value}
          onChange={beaconForm.$.description.onChange}
          className={fieldStyle}
          maxLength={MAX_CHARACTERS_IN_ASSET_DESCRIPTION}
          maxWidth="723px"
        />
      </Vertical>
      <Horizontal horizontalAlign="right" spacing={10}>
        <Button className="biggerButton"
          isLoading={loading}
          outline
          onClick={() => { setSelectedBeaconToCreateAsset(null) }}
          id="cancel-utton">
          Cancel
        </Button>
        <Button className="biggerButton"
          onClick={onImportAndCreate}
          isLoading={loading}
          id="save-button"
          disabled={beaconForm.hasError}
        >
          Import & Create
        </Button>
      </Horizontal>
    </ScrollingContainer>
  )
})

const fieldStyle = style({
  minWidth: "350px",
  width: "350px",
});

const assetFormStyle = (isMobile: boolean) => style({
  backgroundColor: colours.white,
  padding: isMobile ? "20px" : "40px",
  $nest: {
    "td": {
      verticalAlign: "top"
    },
    ".title": {
      color: colours.secondaryText
    }
  }
})

const seperatorStyle = style({
  borderTop: `1px dashed ${colours.border}`,
  borderBottom: '0px',
  borderLeft: '0px',
  borderRight: '0px',
})